import { Alert, Icon } from '@innovationnorway/cl-react';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { TextArea } from '@innovationnorway/component-library';
import { Button } from '@innovationnorway/cl-react';
import DialogContainer from 'src/components/DialogContainer/DialogContainer';
import DialogActions from 'src/components/DialogActions/DialogActions';
import { useUser } from 'src/features/authorization';
import { OneWayInOpportunitySimpleV2Dto, OneWayInStatus } from 'src/api/v2';
import useOneWayIn from 'src/features/one-way-in/hooks/use-one-way-in';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
interface BatchArchivingModalProps {
  showDialog: boolean;
  setShowDialog: Dispatch<SetStateAction<boolean>>;
  selectedRows: OneWayInOpportunitySimpleV2Dto[];
}
const BatchArchivingModal = ({ showDialog, setShowDialog, selectedRows }: BatchArchivingModalProps) => {
  const [comment, setComment] = useState('');
  const { displayName, user, isAadUser } = useUser();
  const { batchArchiveOneWayInOpportunity } = useOneWayIn('');
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { t: tCommon } = useTranslation('common');
  const ToastContent = ({ archivedCount }: { archivedCount: number }) => {
    return (
      <div className="display--flex gap--2">
        <Icon prefix="fasr" iconName="check-circle" className="color--in-green-200" />
        <div>
          <div className="margin-bottom--0 font-size--large font-weight--bold color--in-black-100">
            {archivedCount > 1
              ? tOneWayIn('batchArchive.toast.title.plural', { count: archivedCount })
              : tOneWayIn('batchArchive.toast.title.singular', { count: archivedCount })}
          </div>
          <div className="color--in-black-100 font-size--medium">
            {archivedCount > 1
              ? tOneWayIn('batchArchive.toast.text.plural', { count: archivedCount })
              : tOneWayIn('batchArchive.toast.text.singular', { count: archivedCount })}
          </div>
        </div>
      </div>
    );
  };
  const archiveOpportunity = async (
    oneWayInId: string,
    oneWayInOpportunityId: string,
    municipality: string,
    title: string,
    customerName: string,
    submittedOn: Date | undefined,
    organizationName?: string,
    organizationNumber?: string,
  ) => {
    await batchArchiveOneWayInOpportunity.mutateAsync({
      oneWayInId: oneWayInId,
      municipality: municipality,
      title: title,
      customerName: customerName,
      oneWayInOpportunityId: oneWayInOpportunityId,
      referenceClusterId: undefined,
      description: comment,
      status: OneWayInStatus.Closed,
      closedReason: undefined,
      registeredServices: [],
      recommendedServices: [],
      clusterUserId: isAadUser ? undefined : user?.clusterUserId,
      clusterContactId: undefined,
      handlersName: displayName,
      sendFollowUpReminderOn: undefined,
      admittedToProgram: false,
      submittedOn: submittedOn,
      organizationName: organizationName,
      organizationNumber: organizationNumber,
      sivaPhase: undefined,
      deliveredOn: undefined,
    });
  };
  const batchArchive = () => {
    const promises: Promise<void>[] = [];
    selectedRows.forEach((row) => {
      promises.push(
        archiveOpportunity(
          row.oneWayInId,
          row.id,
          row.municipality ?? '',
          row.title,
          row.customerName ?? '',
          row.submittedOn,
          row.organizationName,
          row.organizationNumber,
        ),
      );
    });
    Promise.all(promises).then(() => {
      setShowDialog(false);
      setComment('');
      toast(<ToastContent archivedCount={selectedRows.length} />, {
        toastId: 'ArchiveSuccessToast',
        autoClose: false,
        position: 'bottom-center',
      });
    });
  };
  return (
    <DialogContainer
      show={showDialog}
      onClose={() => setShowDialog(false)}
      title={tOneWayIn('batchArchive.modal.title')}
    >
      <p>
        {selectedRows.length > 1
          ? tOneWayIn('batchArchive.modal.text.plural')
          : tOneWayIn('batchArchive.modal.text.singular')}
      </p>
      <TextArea
        label={`${tCommon('note')} *`}
        aria-required="true"
        onChange={(e) => setComment(e.target.value)}
        value={comment}
        maxCount={1000}
        maxLength={1000}
      />
      <div className="display--flex flex-direction--column gap--2">
        {batchArchiveOneWayInOpportunity.isError && (
          <Alert data-color="danger">
            <span>
              {tOneWayIn('batchArchive.notification.error.part1')}
              <b>{tOneWayIn('batchArchive.notification.error.error')}</b>
              {tOneWayIn('batchArchive.notification.error.part2')}
            </span>
          </Alert>
        )}
        <DialogActions>
          <Button
            type="button"
            disabled={comment.trim() === ''}
            onClick={() => batchArchive()}
            loading={batchArchiveOneWayInOpportunity.isLoading}
          >
            {tOneWayIn('batchArchive.archive')}
          </Button>
          <Button type="button" variant="secondary" onClick={() => setShowDialog(false)}>
            {tCommon('cancel')}
          </Button>
        </DialogActions>
      </div>
    </DialogContainer>
  );
};
export default BatchArchivingModal;
